import React, { useState } from 'react';
import { useTranslation } from '@whoop/i18n/lang/client';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '../InputV2/Input';
import { Form, FormControl, FormField, FormItem } from '../Form/Form';
import { Button, UnstyledButton } from '../ButtonV2/Button';
import type { UserDetails } from '../../types';
import { PasswordInput } from '../PasswordInput/PasswordInput';
import clsx from 'clsx';

export enum SignInFormVariant {
  DEFAULT = 'default',
  UPGRADE = 'upgrade',
}

const formStyle = {
  upgrade: '',
  default: 'px-3',
};

const submitButtonStyle = {
  upgrade: 'rounded-2xl',
  default: '',
};

type buttonVariant = 'primary' | 'secondary';
const submitButtonVariant: Record<SignInFormVariant, buttonVariant> = {
  upgrade: 'secondary',
  default: 'primary',
};

export interface SignInFormProps {
  onSubmit: (user: UserDetails) => Promise<void>;
  loading?: boolean;
  variant?: SignInFormVariant;
}

export function SignInForm({
  onSubmit,
  loading = false,
  variant = SignInFormVariant.DEFAULT,
}: SignInFormProps): React.JSX.Element {
  const { t } = useTranslation('signInForm');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const formSchema = z.object({
    email: z.string().min(1),
    password: z.string().min(1),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const {
    formState: { isSubmitting },
    handleSubmit,
    control,
  } = form;

  const handleOnSubmit = async (
    data: z.infer<typeof formSchema>,
  ): Promise<void> => {
    const { email, password } = data;
    if (email.length > 0 && password.length > 0) {
      await onSubmit({ email, password });
    }
  };

  return (
    <Form {...form}>
      <form
        className={clsx(
          'flex h-fit flex-col items-center gap-6 py-5',
          formStyle[variant],
        )}
        id='sign-in-form'
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        <FormField
          control={control}
          key='email'
          name='email'
          render={({ field }) => (
            <div className='w-full'>
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    aria-label='Email'
                    autoComplete='email'
                    id='email'
                    inputMode='email'
                    label={t('email')}
                    type='email'
                    value={field.value}
                  />
                </FormControl>
              </FormItem>
            </div>
          )}
        />
        <FormField
          control={control}
          key='password'
          name='password'
          render={({ field }) => (
            <div className='w-full'>
              <FormItem>
                <FormControl>
                  <PasswordInput
                    {...field}
                    aria-label='Password'
                    autoComplete='password'
                    label={t('password')}
                  />
                </FormControl>
              </FormItem>
            </div>
          )}
        />
        <a
          href='https://www.app.whoop.com/forgot-password'
          rel='noopener noreferrer'
          target='_blank'
          className='text-sm font-semibold underline'
          aria-label={`${t('forgotPassword')}?`}
        >
          {t('forgotPassword')}
        </a>
        <Button
          className={clsx('w-full', submitButtonStyle[variant])}
          disabled={loading || isSubmitting}
          form='sign-in-form'
          size='medium'
          type='submit'
          variant={submitButtonVariant[variant]}
        >
          {loading || isSubmitting ? t('loading') : t('signIn')}
        </Button>
      </form>
    </Form>
  );
}
